import { Component, ChangeDetectionStrategy, computed, effect, input, signal, untracked, model } from '@angular/core';
import { IClientPaymentConfig } from 'src/app/models';
import { NotificationTypes, PaymentGateway } from 'src/app/models/enums';
import { ClientsService, UtilsService } from 'src/app/services';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { images } from 'src/images';
import { FormsModule } from '@angular/forms';

import { FormActionsComponent } from 'src/app/shared/form-actions/form-actions.component';
@Component({
  selector: 'app-client-payment-config',
  templateUrl: './client-payment-config.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, FormActionsComponent],
})
export class ClientPaymentConfigComponent {
  readonly clientId = input.required<number>();
  readonly edit = model(false);

  public images = images;
  public PaymentGateway = PaymentGateway;
  public paymentGateways = Object.values(PaymentGateway);

  readonly loading = signal(false);
  readonly loaded = signal(false);
  readonly saving = signal(false);
  readonly redsysSecretKeyConfigured = signal(false);
  readonly paymentGateway = signal<PaymentGateway>(PaymentGateway.STRIPE);
  readonly stripeId = signal('');
  readonly redsysMerchantCode = signal('');
  readonly redsysTerminal = signal('');
  readonly redsysSecretKey = signal('');

  private savedPaymentConfig?: IClientPaymentConfig;

  readonly redsysSecretKeyRequired = computed(
    () => this.paymentGateway() === PaymentGateway.REDSYS && !this.redsysSecretKeyConfigured(),
  );

  readonly isValid = computed(() => {
    if (this.paymentGateway() !== PaymentGateway.REDSYS) return true;
    return (
      !!this.redsysMerchantCode().trim() &&
      !!this.redsysTerminal().trim() &&
      (!this.redsysSecretKeyRequired() || !!this.redsysSecretKey().trim())
    );
  });

  constructor(
    public literalService: LiteralService,
    private clientsService: ClientsService,
    private utilsService: UtilsService,
  ) {
    effect(() => {
      const clientId = this.clientId();
      untracked(() => this.loadPaymentConfig(clientId));
    });
  }

  async loadPaymentConfig(clientId: number) {
    this.loading.set(true);
    this.loaded.set(false);
    try {
      const paymentConfig = await this.clientsService.getPaymentConfig(clientId);
      if (clientId !== this.clientId()) return;
      this.applyPaymentConfig(paymentConfig);
      this.loaded.set(true);
    } catch {
      return;
    } finally {
      if (clientId === this.clientId()) this.loading.set(false);
    }
  }

  applyPaymentConfig(paymentConfig: IClientPaymentConfig) {
    this.savedPaymentConfig = paymentConfig;
    this.paymentGateway.set(paymentConfig.paymentGateway ?? PaymentGateway.STRIPE);
    this.stripeId.set(paymentConfig.stripeId ?? '');
    this.redsysMerchantCode.set(paymentConfig.redsysMerchantCode ?? '');
    this.redsysTerminal.set(paymentConfig.redsysTerminal ?? '');
    this.redsysSecretKeyConfigured.set(!!paymentConfig.redsysSecretKeyConfigured);
    this.redsysSecretKey.set('');
  }

  cancel() {
    if (this.savedPaymentConfig) this.applyPaymentConfig(this.savedPaymentConfig);
    this.edit.set(false);
  }

  readInputValue(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  async save() {
    if (!this.isValid() || this.saving()) return;
    this.saving.set(true);
    try {
      const paymentConfig = await this.clientsService.updatePaymentConfig(this.clientId(), {
        paymentGateway: this.paymentGateway(),
        stripeId: this.stripeId(),
        redsysMerchantCode: this.redsysMerchantCode(),
        redsysTerminal: this.redsysTerminal(),
        redsysSecretKey: this.redsysSecretKey() || null,
      });
      this.applyPaymentConfig(paymentConfig);
      this.edit.set(false);
      this.utilsService.showNotification(images.notifications.check, 'clients.paymentConfig.saved', NotificationTypes.SUCCESS);
    } catch {
      return;
    } finally {
      this.saving.set(false);
    }
  }
}
